import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    HostListener,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
    Bell,
    Bot,
    Check,
    LucideAngularModule,
    LucideIconData,
    PanelTopClose,
    PanelTopOpen,
    Sparkles,
} from 'lucide-angular';
import { filter, map } from 'rxjs';

import { TRANSLATION_KEYS } from '@core/internationalization/translations_keys';
import { UserMenuComponent } from '@core/layout/user-menu/user-menu.component';
import { SIDEBAR_ICONS } from '@core/layout/sidebar/sidebar.config';
import { AccountService } from '@core/services/admin/account.service';
import { LoggedUserService } from '@core/services/admin/logged-user.service';
import { AgentChatPanelService } from '@features/agent-chat/services/agent-chat-panel.service';
import { NewsletterModalComponent } from '@features/dashboard/newsletter/components/newsletter-modal/newsletter-modal.component';
import { NewsletterPost } from '@features/dashboard/newsletter/models/newsletter.types';
import { NewsletterService } from '@features/dashboard/newsletter/services/newsletter.service';
import { InbInfoBarStateService } from '@features/inbox/components/inb-info-bar/inb-info-bar-state.service';
import { BillingService } from '@features/settings/billing/services/billing.service';
import { MAIN_SLUGS } from '@routes/routes.types';
import { ImageErrorDirective } from '@shared/directives/image-error.directive';
import { UserAvatarComponent } from '@shared/components/user-avatar/user-avatar.component';

/**
 * Global slim top bar. Mounted once in the app shell (main-content) and stays sticky
 * at the top of the main scroll container, so it persists across every authenticated route.
 *
 * Left:  title of the section the user is currently inside, derived from the URL.
 * Right: Twinny AI button (opens the slide-in panel — replaces the floating launcher),
 *        a bell with a popover of product news / alerts, and the user avatar with the
 *        settings popover (Saldo / Workspace / Configuración / Cerrar sesión).
 */
@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [
        LucideAngularModule,
        TranslateModule,
        UserAvatarComponent,
        UserMenuComponent,
        NewsletterModalComponent,
        ImageErrorDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'block' },
    styles: [
        `
            /* Animated blue→purple gradient border for the Twinny AI button.
               A masked ::before paints only a 1.5px frame; the conic-gradient angle
               is animated via a registered custom property so the sweep rotates. */
            @property --twinny-angle {
                syntax: '<angle>';
                initial-value: 0deg;
                inherits: false;
            }
            .twinny-ai-btn::before {
                content: '';
                position: absolute;
                inset: 0;
                border-radius: inherit;
                padding: 1.5px;
                background: conic-gradient(
                    from var(--twinny-angle),
                    #e4e4e7 0deg,
                    #ffffff 45deg,
                    #3157da 130deg,
                    #6366f1 175deg,
                    #a855f7 215deg,
                    #ffffff 300deg,
                    #e4e4e7 360deg
                );
                -webkit-mask:
                    linear-gradient(#000 0 0) content-box,
                    linear-gradient(#000 0 0);
                -webkit-mask-composite: xor;
                mask-composite: exclude;
                animation: twinny-border-rotate 4s linear infinite;
            }
            @keyframes twinny-border-rotate {
                to {
                    --twinny-angle: 360deg;
                }
            }
            @media (prefers-reduced-motion: reduce) {
                .twinny-ai-btn::before {
                    animation: none;
                }
            }
        `,
    ],
    template: `
        <header
            class="flex h-9 shrink-0 items-center justify-between border-b border-zinc-200/80 bg-white/85 px-4 backdrop-blur-md">
            <!-- Current section: menu icon + title -->
            <div class="flex min-w-0 items-center gap-2">
                @if (sectionIcon(); as icon) {
                    <lucide-icon
                        class="shrink-0 text-zinc-900/55"
                        [img]="icon"
                        [size]="14"
                        [strokeWidth]="1.75" />
                }
                <h1
                    class="truncate py-px text-[13px] font-medium tracking-[-0.005em] text-zinc-900">
                    {{ sectionTitleKey() | translate }}
                </h1>

                <!-- Inbox only: show / hide the summary bar (unread, tasks, next item) -->
                @if (isInbox()) {
                    @let barHidden = infoBarS.isHidden();
                    <span class="mx-1.5 h-4 w-px shrink-0 bg-zinc-200/80" aria-hidden="true"></span>
                    <button
                        class="flex h-7 shrink-0 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium transition-[background-color,color] duration-200 ease-out-expo hover:bg-zinc-100 hover:text-zinc-900"
                        [class.bg-zinc-100]="!barHidden"
                        [class.text-zinc-500]="barHidden"
                        [class.text-zinc-900]="!barHidden"
                        type="button"
                        [attr.aria-pressed]="!barHidden"
                        [title]="
                            (barHidden ? T.engage.infoBar.show : T.engage.infoBar.hide) | translate
                        "
                        (click)="infoBarS.toggle()">
                        <lucide-icon
                            [img]="barHidden ? PanelTopOpenIcon : PanelTopCloseIcon"
                            [size]="14"
                            [strokeWidth]="1.75" />
                        <span class="tracking-[-0.005em]">{{
                            T.engage.infoBar.summary | translate
                        }}</span>
                        @if (barHidden && infoBarS.unreadCount() > 0) {
                            <span
                                class="rounded-full bg-primary-600 px-1.5 font-mono text-[10px] font-semibold tabular-nums leading-[16px] text-white">
                                {{ infoBarS.unreadCount() }}
                            </span>
                        }
                    </button>
                }
            </div>

            <!-- Right cluster: Twinny AI + Bell + Avatar -->
            <div class="flex items-center gap-0.5">
                @if (assistantEnabled()) {
                    <button
                        class="twinny-ai-btn relative mr-1 flex h-7 items-center gap-1.5 rounded-lg pl-2 pr-1.5 text-[12px] font-medium text-zinc-700 transition-[background-color,color] duration-200 ease-out-expo hover:bg-zinc-50 hover:text-zinc-900"
                        [class.bg-zinc-100]="panelS.isOpen()"
                        [class.text-zinc-900]="panelS.isOpen()"
                        type="button"
                        [attr.aria-label]="T.agentChat.menu | translate"
                        (click)="panelS.toggle()">
                        <lucide-icon
                            class="text-zinc-900"
                            [img]="SparklesIcon"
                            [size]="14"
                            [strokeWidth]="1.85" />
                        <span class="tracking-[-0.005em]">{{ T.agentChat.menu | translate }}</span>
                        <span
                            class="rounded-full bg-primary-50 px-1.5 py-px text-[9px] font-semibold uppercase leading-none tracking-[0.08em] text-primary-600 ring-1 ring-inset ring-primary-100">
                            beta
                        </span>
                    </button>
                }

                <div class="relative">
                    <button
                        class="relative flex size-7 items-center justify-center rounded-lg text-zinc-500 ring-1 ring-inset ring-zinc-200/70 transition-[background-color,color] duration-200 ease-out-expo hover:bg-zinc-100 hover:text-zinc-900"
                        [class.bg-zinc-100]="bellOpen()"
                        [class.text-zinc-900]="bellOpen()"
                        type="button"
                        [attr.aria-label]="T.dashboard.newsletter.bell.title | translate"
                        [title]="T.dashboard.newsletter.bell.title | translate"
                        (click)="toggleBell($event)">
                        <lucide-icon [img]="BellIcon" [size]="15" [strokeWidth]="1.75" />
                        @if (unreadCount() > 0) {
                            <span
                                class="pointer-events-none absolute right-1 top-1 size-[5px] rounded-full bg-primary-500 ring-2 ring-white"></span>
                        }
                    </button>

                    @if (bellOpen()) {
                        <div
                            class="absolute right-0 top-[calc(100%+8px)] z-50 w-[360px] origin-top-right animate-rise-fade overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-[0_18px_48px_-18px_rgba(15,23,42,0.18),0_2px_8px_-3px_rgba(15,23,42,0.08)]"
                            (click)="$event.stopPropagation()">
                            <!-- Header -->
                            <div
                                class="flex items-center justify-between border-b border-zinc-100 px-4 pb-2.5 pt-3">
                                <div class="flex items-baseline gap-2">
                                    <h2
                                        class="text-[13px] font-semibold tracking-[-0.01em] text-zinc-900">
                                        {{ T.dashboard.newsletter.bell.title | translate }}
                                    </h2>
                                    @if (unreadCount() > 0) {
                                        <span
                                            class="text-[10.5px] font-medium uppercase tracking-[0.14em] text-primary-600">
                                            {{ unreadCount() }}
                                            {{ T.dashboard.newsletter.bell.new | translate }}
                                        </span>
                                    }
                                </div>
                                @if (unreadCount() > 0) {
                                    <button
                                        class="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
                                        type="button"
                                        (click)="markAllRead()">
                                        <lucide-icon
                                            [img]="CheckIcon"
                                            [size]="12"
                                            [strokeWidth]="2" />
                                        {{ T.dashboard.newsletter.bell.markRead | translate }}
                                    </button>
                                }
                            </div>

                            <!-- Items -->
                            @if (posts().length > 0) {
                                <ul class="max-h-[400px] overflow-y-auto py-1">
                                    @for (post of posts(); track post.id) {
                                        <li>
                                            <button
                                                class="group relative flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-50/80"
                                                type="button"
                                                (click)="openPost(post)">
                                                @if (!isRead(post.id)) {
                                                    <span
                                                        class="absolute left-1.5 top-1/2 size-1.5 -translate-y-1/2 rounded-full bg-primary-500"></span>
                                                }
                                                <figure
                                                    class="relative mt-0.5 size-10 shrink-0 overflow-hidden rounded-lg ring-1 ring-inset ring-zinc-200/70">
                                                    <img
                                                        class="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out-expo group-hover:scale-[1.06]"
                                                        appImageError
                                                        [alt]="post.imgData.alt"
                                                        [src]="post.imgData.src" />
                                                    <span
                                                        class="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_100%_0%,rgba(49,87,218,0.28),transparent_60%)]"></span>
                                                </figure>
                                                <span class="min-w-0 flex-1">
                                                    <span
                                                        class="flex items-baseline justify-between gap-2">
                                                        <span
                                                            class="truncate text-[13px] font-semibold tracking-[-0.005em] text-zinc-900">
                                                            {{ post.titleClean }}
                                                        </span>
                                                        <span
                                                            class="shrink-0 font-mono text-[10px] text-zinc-400">
                                                            v{{ post.version }}
                                                        </span>
                                                    </span>
                                                    <span
                                                        class="mt-0.5 line-clamp-2 block text-[12px] leading-snug text-zinc-500">
                                                        {{ post.description }}
                                                    </span>
                                                    <span
                                                        class="mt-1 block text-[10.5px] text-zinc-400">
                                                        {{ post.releaseDate }}
                                                    </span>
                                                </span>
                                            </button>
                                        </li>
                                    }
                                </ul>
                            } @else {
                                <div
                                    class="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
                                    <span
                                        class="inline-flex size-9 items-center justify-center rounded-xl bg-zinc-100/80 text-zinc-400 ring-1 ring-inset ring-zinc-200/70">
                                        <lucide-icon
                                            [img]="BellIcon"
                                            [size]="16"
                                            [strokeWidth]="1.75" />
                                    </span>
                                    <span class="text-[12.5px] text-zinc-500">
                                        {{ T.dashboard.newsletter.bell.empty | translate }}
                                    </span>
                                </div>
                            }
                        </div>
                    }
                </div>

                <!-- Avatar with usage ring + user popover -->
                <div class="relative ml-1.5">
                    <button
                        class="group/usage relative flex size-7 items-center justify-center rounded-full bg-white transition-[box-shadow,background-color] duration-200 ease-out-expo focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50"
                        [class.ring-2]="userOpen()"
                        [class.ring-zinc-900]="userOpen()"
                        type="button"
                        [attr.aria-label]="T.common.userMenu.open | translate"
                        [title]="usageTitle()"
                        (click)="toggleUser($event)"
                        (mouseenter)="isHovered.set(true)"
                        (mouseleave)="isHovered.set(false)">
                        <!-- progress arc (always visible — glanceable usage hint) -->
                        <svg
                            class="absolute inset-0 size-full -rotate-90"
                            viewBox="0 0 36 36"
                            aria-hidden="true">
                            <circle
                                class="text-zinc-200/90"
                                cx="18"
                                cy="18"
                                fill="none"
                                r="15.25"
                                stroke="currentColor"
                                stroke-width="2" />
                            <circle
                                class="text-zinc-900 transition-[stroke-dasharray] duration-500 ease-out-expo"
                                cx="18"
                                cy="18"
                                fill="none"
                                pathLength="100"
                                r="15.25"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-width="2"
                                [attr.stroke-dasharray]="usagePercent() + ' 100'" />
                        </svg>

                        <!-- Identity (default) -->
                        <tw-user-avatar
                            class="absolute inset-[3px] transition-opacity duration-150 ease-out-expo"
                            [class.opacity-0]="showPercent()"
                            [class.opacity-100]="!showPercent()"
                            size="sm"
                            [name]="userName()"
                            [src]="profileImg()" />

                        <!-- Percentage (hover / open) -->
                        <span
                            class="relative font-mono text-[9px] font-semibold tabular-nums leading-none tracking-[-0.02em] text-zinc-900 transition-opacity duration-150 ease-out-expo"
                            [class.opacity-0]="!showPercent()"
                            [class.opacity-100]="showPercent()">
                            {{ usagePercent() }}%
                        </span>
                    </button>

                    <app-user-menu [isOpen]="userOpen()" (close)="userOpen.set(false)" />
                </div>
            </div>
        </header>

        <!-- Release detail modal — opened from the bell's "What's new" list. -->
        <app-newsletter-modal />
    `,
})
export class TopbarComponent implements OnInit {
    private readonly router = inject(Router);
    private readonly hostRef = inject(ElementRef<HTMLElement>);
    private readonly userS = inject(LoggedUserService);
    private readonly billingS = inject(BillingService);
    private readonly newsletterS = inject(NewsletterService);
    private readonly accountS = inject(AccountService);
    protected readonly panelS = inject(AgentChatPanelService);
    protected readonly infoBarS = inject(InbInfoBarStateService);

    /** TwinnyAI assistant is opt-in per account: only show the launcher when enabled. */
    protected readonly assistantEnabled = computed(
        () => this.accountS.account()?.assistant_enabled === true
    );

    protected readonly T = TRANSLATION_KEYS;
    protected readonly BellIcon = Bell;
    protected readonly PanelTopOpenIcon = PanelTopOpen;
    protected readonly PanelTopCloseIcon = PanelTopClose;
    protected readonly SparklesIcon = Sparkles;
    protected readonly CheckIcon = Check;

    readonly bellOpen = signal(false);
    readonly userOpen = signal(false);
    readonly isHovered = signal(false);

    /** Avatar trigger swaps from photo → percentage when hovered or while the popover is open. */
    readonly showPercent = computed(() => this.isHovered() || this.userOpen());

    protected readonly profileImg = computed(() => this.userS.user()?.avatar);
    protected readonly userName = computed(() => this.userS.user()?.name ?? '');

    /**
     * AI-managed-contacts usage as a percentage of the plan's AI quota —
     * `mac_ai` consumed / `macAiQuota` × 100. Powers the circular indicator that
     * wraps the avatar, and is the SAME metric shown in the user-menu "Consumo"
     * card and the dashboard KPI (so the ring matches the popover it opens).
     * Defaults to 0 when no plan data is loaded yet (renders an empty ring).
     */
    readonly usagePercent = computed(() => {
        const quota = this.billingS.macAiQuota();
        if (!quota || quota <= 0) return 0;
        const consumed = Number(this.billingS.currentMacs()?.['mac_ai'] ?? 0);
        return Math.max(0, Math.min(100, Math.round((consumed / quota) * 100)));
    });

    /** Tooltip on the ring — combines the user's name with the usage percentage. */
    readonly usageTitle = computed(() => {
        const name = this.userName();
        const pct = this.usagePercent();
        return name ? `${name} — ${pct}%` : `${pct}%`;
    });

    /** Product releases — the "What's new" feed surfaced in the bell popover. */
    readonly posts = this.newsletterS.posts;
    readonly unreadCount = this.newsletterS.unreadCount;

    protected isRead(id: number): boolean {
        return this.newsletterS.readIds().has(id);
    }

    openPost(post: NewsletterPost) {
        this.newsletterS.openPost(post);
        this.bellOpen.set(false);
    }

    /**
     * URL of the active route, re-evaluated on every navigation. Used to derive
     * the section title shown on the left side of the bar.
     */
    private readonly url = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(e => e.urlAfterRedirects)
        ),
        { initialValue: this.router.url }
    );

    /**
     * Resolves the active URL to a section descriptor by matching against the known
     * main slugs. When several slugs appear in the path (e.g. `/main/automate/ai-agents`),
     * we prefer the one CLOSEST TO THE ROOT — that's the section the user is inside.
     */
    private readonly section = computed(() => {
        const segments = (this.url() ?? '').split('?')[0].split('/').filter(Boolean);
        for (const seg of segments) {
            const meta = SECTION_BY_SLUG[seg];
            if (meta) return meta;
        }
        return null;
    });

    readonly sectionTitleKey = computed(() => this.section()?.key ?? '');
    /** The inbox is the only section with a page-level summary bar to toggle. */
    readonly isInbox = computed(() =>
        (this.url() ?? '').split('?')[0].split('/').filter(Boolean).includes(MAIN_SLUGS.inbox)
    );
    readonly sectionIcon = computed(() => this.section()?.icon ?? null);

    ngOnInit() {
        // Make sure the plan + meter data is available so the usage ring renders
        // immediately on app start, even if the user never visits Billing.
        if (!this.billingS.currentPlan()) this.billingS.reqCurrentPlan();
        this.billingS.reqMeters();
    }

    toggleBell(e: MouseEvent) {
        e.stopPropagation();
        this.userOpen.set(false);
        this.bellOpen.update(v => !v);
    }

    toggleUser(e: MouseEvent) {
        e.stopPropagation();
        this.bellOpen.set(false);
        this.userOpen.update(v => !v);
    }

    markAllRead() {
        this.newsletterS.markAllRead();
    }

    @HostListener('document:click', ['$event'])
    onDocClick(e: MouseEvent) {
        const outside = !this.hostRef.nativeElement.contains(e.target as Node);
        if (outside) {
            if (this.bellOpen()) this.bellOpen.set(false);
            if (this.userOpen()) this.userOpen.set(false);
        }
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.bellOpen()) this.bellOpen.set(false);
        if (this.userOpen()) this.userOpen.set(false);
    }
}

interface SectionMeta {
    key: string;
    icon: LucideIconData;
}

/**
 * Single source of truth for section title + icon shown in the top bar.
 * Mirrors the items the sidebar renders (sidebar.config.ts → SIDEBAR_ICONS),
 * plus the AI workspace routes that don't live in the sidebar.
 */
const SECTION_BY_SLUG: Record<string, SectionMeta> = {
    [MAIN_SLUGS.DASHBOARD]: {
        key: TRANSLATION_KEYS.common.sections.dashboard,
        icon: SIDEBAR_ICONS.dashboard,
    },
    [MAIN_SLUGS.audience]: {
        key: TRANSLATION_KEYS.common.sections.contacts,
        icon: SIDEBAR_ICONS.audience,
    },
    [MAIN_SLUGS.inbox]: {
        key: TRANSLATION_KEYS.common.sections.engage,
        icon: SIDEBAR_ICONS.inbox,
    },
    [MAIN_SLUGS.campaigns]: {
        key: TRANSLATION_KEYS.common.sections.reach,
        icon: SIDEBAR_ICONS.reach,
    },
    [MAIN_SLUGS.commerce]: {
        key: TRANSLATION_KEYS.common.sections.commerce,
        icon: SIDEBAR_ICONS.commerce,
    },
    [MAIN_SLUGS.automate]: {
        key: TRANSLATION_KEYS.engage.tabs.automation,
        icon: SIDEBAR_ICONS.automation,
    },
    [MAIN_SLUGS.TEMPLATES]: {
        key: TRANSLATION_KEYS.common.sections.templates,
        icon: SIDEBAR_ICONS.templates,
    },
    [MAIN_SLUGS.TICKET]: { key: 'Ticket', icon: SIDEBAR_ICONS.ticket },
    [MAIN_SLUGS.INSIGHTS]: {
        key: TRANSLATION_KEYS.engage.tabs.insights,
        icon: SIDEBAR_ICONS.insights,
    },
    [MAIN_SLUGS.integrations]: {
        key: TRANSLATION_KEYS.settings.tabs.integrations,
        icon: SIDEBAR_ICONS.integrations,
    },
    [MAIN_SLUGS.SETTINGS]: {
        key: TRANSLATION_KEYS.common.sections.settings,
        icon: SIDEBAR_ICONS.settings,
    },
    [MAIN_SLUGS.aiAgents]: { key: TRANSLATION_KEYS.common.sections.aiAgents, icon: Bot },
    [MAIN_SLUGS.agentChat]: { key: TRANSLATION_KEYS.agentChat.menu, icon: Sparkles },
};
